import { AssembleiaIcon } from 'components/Icons';
import DialogAlert from 'components/Icons/DialogAlert';
import LayoutContext from 'components/Layout/LayoutContext';
import { clsx } from 'helpers/utils/HTMLUtils';
import { languageValues } from 'helpers/utils/languageUtils';
import { toRequest } from 'helpers/utils/RequestUtils';
import { onlyNumbers } from 'helpers/utils/StringUtils';
import { getAssembleiasAssessorEndpoint } from 'library/Apis';
import translation from 'locales/translation';
import {
  Button,
  ButtonThemes,
  Checkbox,
  CheckCircleIcon,
  ChevronArrowDownIcon,
  Col,
  Collapse,
  ControlLabel,
  Etiqueta,
  EtiquetasStyle,
  Input,
  RadioButton,
  RadioButtonType,
  Row,
  TimesCircleIcon,
  ToastTypes,
  TooltipManager,
  TooltipPosition,
} from 'plataforma-fundacao-componentes';
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import ApiService from 'services/ApiService';
import {
  allowAvailability,
  checkAvailability,
  inPersonVote,
} from 'services/assessor';
import ComputeVotesReducer, {
  INITIAL_STATE,
} from 'store/modules/ComputeVotesReducer';
import { COMPUTE_VOTES_PRESENCE_VOTES } from 'store/modules/ConfirmarPresencaActions';
import { IReducer } from 'store/Reducer';
import './styles.scss';
import { useDebouncedCallback } from 'use-debounce';

const rootClassName = 'component-list-votes';

export enum STATUS_LIST_VOTES {
  NAO_INICIADA = 'NAO_INICIADA',
  INICIADA = 'INICIADA',
  CONCLUIDA = 'CONCLUIDA',
  IMPORTADA = 'IMPORTADA',
}

interface IProps {
  agencia: string;
  nucleo: string;
  idPauta: number;
  opened: boolean;
  totalAssociados: number;
  borderColor: STATUS_LIST_VOTES;
  toggleOpened: (evt: any) => void;
  status: STATUS_LIST_VOTES;
  ldap: string | null;
  itens: [
    {
      idItemPauta: number;
      descricao: string;
      quantidadeVotos: number | null;
      flgAclamacao: boolean;
    }
  ];
  onVoteButtonSave?: () => Promise<void>;
  onRefresh?: any;
  dataTestId?: string;
  disabledAclamacao?: boolean;
  disabledNoVote?: boolean;
  registerAllNucleous?: boolean;
  isReadOnly: boolean;
  flagSemVotos: boolean;
}

type InputProps = {
  [index: number]: {
    flagAclamacao: boolean;
    idItemPauta: number;
    quantidade: number;
  };
};

type ItemProps = {
  idItemPauta: number;
  descricao: string;
  quantidadeVotos: number | null;
  flgAclamacao: boolean;
};

export const ListVotes = (props: IProps) => {
  const [loadingVoto, setLoadingVoto] = useState<boolean>(false);
  const [defaultValues, setDefaultValues] = useState<
    [
      {
        idItemPauta: number;
        descricao: string;
        quantidadeVotos: number | null;
        flgAclamacao: boolean;
      }
    ]
  >();
  const [computeVote, hasComputeVote] = useState<boolean>(
    !props.disabledAclamacao && props.flagSemVotos
  );
  const [computeVoteIsDirty, setComputeVoteIsDirty] = useState<boolean>(false);
  const [semManifestacaoVoto, setSemManifestacaoVoto] = useState<boolean>(
    props.flagSemVotos
  );
  const [manifestacaoVotoIsDirty, setManifestacaoVotoIsDirty] =
    useState<boolean>(false);
  const [idItemPauta, setIdItemPauta] = useState<number>();
  const [type, hasType] = useState<boolean>(false);
  const [disponibility, setDisponibility] = useState<boolean>(true);
  const [inputValue, setInputValue] = useState<InputProps>({});
  const [aclamacao, setAclamacao] = useState<boolean>(false);
  const [aclamacaoValue, setAclamacaoValue] = useState<number | undefined>(
    undefined
  );

  const [state, dispatch] = useReducer(ComputeVotesReducer, INITIAL_STATE);
  const { usuario } = useSelector((state: IReducer) => state.auth);
  const { showToast } = useContext(LayoutContext);

  const handleToggle = () => {
    if (typeof props.toggleOpened === 'function') {
      props.toggleOpened(!props.opened);
    }
  };

  useEffect(() => {
    setDefaultValues(props.itens);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.registerAllNucleous) {
      setAclamacao(false);
      hasComputeVote(false);
      setAclamacaoValue(undefined);
      setIdItemPauta(undefined);
      setInputValue({});
    }
  }, [props.registerAllNucleous]);

  const getStatusName = (status: STATUS_LIST_VOTES) => {
    switch (status) {
      case STATUS_LIST_VOTES.NAO_INICIADA:
        return 'Não há votos';
      case STATUS_LIST_VOTES.INICIADA:
        return 'Em preenchimento';
      case STATUS_LIST_VOTES.CONCLUIDA:
        return 'Votação concluída';
      case STATUS_LIST_VOTES.IMPORTADA:
        return 'Votação registrada';
      default:
        return 'Não há votos';
    }
  };

  const getBorderColor = (status: STATUS_LIST_VOTES) => {
    switch (status) {
      case STATUS_LIST_VOTES.NAO_INICIADA:
        return 'grey';
      case STATUS_LIST_VOTES.INICIADA:
        return 'yellow';
      case STATUS_LIST_VOTES.CONCLUIDA:
        return 'green';
      case STATUS_LIST_VOTES.IMPORTADA:
        return 'green';
      default:
        return 'grey';
    }
  };

  const getStatusTheme = (status: STATUS_LIST_VOTES) => {
    switch (status) {
      case STATUS_LIST_VOTES.NAO_INICIADA:
        return EtiquetasStyle.Finalizado;
      case STATUS_LIST_VOTES.INICIADA:
        return EtiquetasStyle.Aprovacao;
      case STATUS_LIST_VOTES.CONCLUIDA:
        return EtiquetasStyle.Edicao;
      case STATUS_LIST_VOTES.IMPORTADA:
        return EtiquetasStyle.Edicao;
      default:
        return EtiquetasStyle.Finalizado;
    }
  };

  const handleOnChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
    index: number,
    idItemPauta: number
  ) => {
    let newObject = { ...inputValue };
    newObject[index] = {
      idItemPauta: idItemPauta,
      quantidade: isNaN(evt.target.value as any)
        ? 0
        : Number(onlyNumbers(evt.target.value)),
      flagAclamacao: aclamacao,
    };

    const updateVote: any = props.itens.find(
      (element) => element.idItemPauta === idItemPauta
    );

    updateVote.quantidadeVotos = isNaN(evt.target.value as any)
      ? 0
      : Number(evt.target.value);

    setComputeVoteIsDirty(false);
    setManifestacaoVotoIsDirty(false);

    dispatch({
      type: COMPUTE_VOTES_PRESENCE_VOTES,
      payload: {
        agencia: props.agencia,
        flagSemVotos: false,
        idPauta: props.idPauta,
        itens: props.itens.map((item: ItemProps) => {
          if (item.idItemPauta !== idItemPauta) {
            return {
              idItemPauta: item.idItemPauta,
              quantidade: item.quantidadeVotos,
              flagAclamacao: aclamacao,
            };
          }

          return {
            idItemPauta: item.idItemPauta,
            quantidade: updateVote?.quantidadeVotos,
            flagAclamacao: aclamacao,
          };
        }),
        nucleo: props.nucleo,
      },
    });

    setInputValue(newObject);
  };

  const sumAssociados: any = (obj: number, byProp?: boolean) =>
    Object.values(obj).reduce(
      (a: number, b: { quantidade: number; quantidadeVotos: number }) => {
        return !byProp ? a + b.quantidade : a + b.quantidadeVotos;
      },
      0
    );

  const everyZero = useMemo(() => {
    if (state.presenceVote && state.presenceVote.itens) {
      return state.presenceVote.itens.every(
        (x: { quantidade: number }) => x.quantidade === 0
      );
    }
  }, [state.presenceVote]);

  const disabledSave = useMemo(() => {
    if (props.disabledAclamacao && props.flagSemVotos) {
      return semManifestacaoVoto || everyZero;
    }

    if (semManifestacaoVoto && manifestacaoVotoIsDirty) {
      return false;
    }

    return (
      props.registerAllNucleous ||
      !state.presenceVote?.itens ||
      (everyZero && !computeVote) ||
      (state.presenceVote &&
        sumAssociados(state.presenceVote?.itens ?? 0) >
          props.totalAssociados) ||
      loadingVoto
    );
  }, [
    computeVote,
    everyZero,
    loadingVoto,
    manifestacaoVotoIsDirty,
    props.disabledAclamacao,
    props.flagSemVotos,
    props.registerAllNucleous,
    props.totalAssociados,
    semManifestacaoVoto,
    state.presenceVote,
  ]);

  useEffect(() => {
    if (!props.opened) {
      dispatch({
        type: COMPUTE_VOTES_PRESENCE_VOTES,
        payload: undefined,
      });
      setInputValue({});
      hasComputeVote(false);
      setAclamacaoValue(undefined);
      setAclamacao(false);
    } else {
      hasComputeVote(props.flagSemVotos);
      setSemManifestacaoVoto(props.flagSemVotos);
    }
  }, [props.flagSemVotos, props.opened]);

  useEffect(() => {
    if (state.presenceVote && sumAssociados(state.presenceVote!?.itens!) > 0) {
      hasComputeVote(false);
    }
  }, [state.presenceVote]);

  useEffect(() => {
    if (aclamacao) {
      dispatch({
        type: COMPUTE_VOTES_PRESENCE_VOTES,
        payload: undefined,
      });
      setInputValue({});
      hasComputeVote(false);
    } else {
      hasComputeVote(props.flagSemVotos);
    }
  }, [aclamacao, props.flagSemVotos]);

  useEffect(() => {
    if (computeVote) {
      dispatch({
        type: COMPUTE_VOTES_PRESENCE_VOTES,
        payload: {
          agencia: props.agencia,
          flagSemVotos: computeVote,
          idPauta: props.idPauta,
          itens: props.itens.map((item: ItemProps) => {
            return {
              flagAclamacao: aclamacao,
              idItemPauta: item.idItemPauta,
              quantidade: 0,
            };
          }),
          nucleo: props.nucleo,
        },
      });
    } else if (computeVoteIsDirty) {
      if (!props.disabledAclamacao) {
        dispatch({
          type: COMPUTE_VOTES_PRESENCE_VOTES,
          payload: undefined,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computeVote]);

  const updateDebouncedInputValue = useDebouncedCallback((cb: () => void) => {
    cb();
  }, 1000);

  const useOnceCall = (cb: () => void, condition: number | boolean = false) => {
    useEffect(() => {
      if (condition && !type) {
        updateDebouncedInputValue(cb);
      }
    }, [cb, condition]);
  };

  useOnceCall(() => {
    if (disponibility && !type) {
      checkAvailability({
        agencia: props.agencia,
        idPauta: props.idPauta,
        nucleo: props.nucleo,
      })
        .then(() => {
          hasType(true);
        })
        .catch((err) => {
          setTimeout(() => {
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label: languageValues.toasts.error.keyErrors(
                err.response?.data.message
              ),
            });
          }, 500);
          props.onRefresh();
          props.toggleOpened(false);
        })
        .finally(() => {
          setDisponibility(false);
        });
    }
  }, Object.keys(inputValue).length);

  const handleCheckAvailability = useCallback(
    () =>
      checkAvailability({
        agencia: props.agencia,
        idPauta: props.idPauta,
        nucleo: props.nucleo,
      })
        .catch((err) => {
          setTimeout(() => {
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label: languageValues.toasts.error.keyErrors(
                err.response?.data.message
              ),
            });
          }, 500);
          props.onRefresh();
          props.toggleOpened(false);
        })
        .finally(() => {
          setDisponibility(false);
        }),
    [props, showToast]
  );

  useEffect(() => {
    if (disponibility && aclamacao) {
      //está chamando depois que faz a declaração de que não há votos
      handleCheckAvailability();
    }
  }, [aclamacao, computeVote, disponibility, handleCheckAvailability]);

  useEffect(() => {
    hasType(false);
    if (!props.opened && !disponibility) {
      allowAvailability({
        agencia: props.agencia,
        idPauta: props.idPauta,
        nucleo: props.nucleo,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.opened]);

  useEffect(() => {
    dispatch({
      type: COMPUTE_VOTES_PRESENCE_VOTES,
      payload: undefined,
    });
  }, [props.onRefresh]);

  const handleSaveVote = () => {
    setLoadingVoto(true);

    const stopLoader = () => setLoadingVoto(false);

    const onSuccess = () => {
      props.toggleOpened(false);
      setTimeout(() => {
        if (props.onVoteButtonSave) {
          props.onVoteButtonSave();
        }
      }, 500);
    };

    const onError = (err: any) => {
      showToast({
        pauseOnFocusLoss: true,
        theme: ToastTypes.Error,
        showStatusBar: true,
        timeout: 4000,
        label: languageValues.toasts.error.keyErrors(
          err.response?.data.message
        ),
      });
    };

    if (semManifestacaoVoto && props.disabledAclamacao) {
      const url = `${getAssembleiasAssessorEndpoint(
        '1'
      )}voto-presencial/evento-pauta-voto-lote`;

      const parsedUrl = new URL(window.location.href);

      const eventId = parsedUrl.pathname.match(/evento\/(\d+)/)?.[1];

      const params = {
        agsNucleos: [
          {
            codigoAgencia: props.agencia,
            numeroNucleo: props.nucleo,
          },
        ],
        semManifestacaoVoto: true,
        idEvento: eventId,
        idPauta: props.idPauta,
        tipoVotoLote: 0,
      };

      toRequest(ApiService.post, [url, params], 'alterarVotosNucleos')
        .then(onSuccess)
        .catch(onError)
        .finally(stopLoader);
    } else {
      state.presenceVote &&
        inPersonVote(state.presenceVote)
          .then(onSuccess)
          .catch(onError)
          .finally(stopLoader);
    }
  };

  return (
    <Row
      className={clsx([rootClassName, getBorderColor(props.borderColor)])}
      data-testid={props.dataTestId}
    >
      <header
        onClick={() => handleToggle()}
        className={props.opened ? 'active' : ''}
      >
        <div className={`${rootClassName}-agn-nuc-container`}>
          <p>
            <strong>{`${translation.ComputeVotes.filter.agencia}:`}</strong>{' '}
            {props.agencia}
          </p>
          <p>
            <strong>{`${translation.ComputeVotes.filter.nucleo}:`}</strong>{' '}
            {props.nucleo}
          </p>
        </div>
        <div
          className={clsx([
            `${rootClassName}-status-chevron-container`,
            props.opened ? 'toggle' : '',
          ])}
        >
          <div
            tooltip-text={
              props.status === STATUS_LIST_VOTES.INICIADA
                ? `Preenchimento da votação em andamento por: ${props.ldap}`
                : ''
            }
            tooltip-position={
              props.status === STATUS_LIST_VOTES.INICIADA
                ? TooltipPosition.Top
                : undefined
            }
          >
            <Etiqueta
              icon={<AssembleiaIcon />}
              label={getStatusName(props.status)}
              theme={getStatusTheme(props.status)}
            />
          </div>
          <TooltipManager />
          <ChevronArrowDownIcon />
        </div>
      </header>
      <Row
        className={clsx([
          `${rootClassName}-collapse-container`,
          props.opened ? 'active' : '',
        ])}
      >
        <Collapse opened={props.opened} animateOpacity>
          <Row className={`${rootClassName}-votes-list`}>
            {aclamacao ? (
              props.itens!?.map((item, index: number) => {
                return (
                  <Col
                    onClick={(val: React.MouseEvent<HTMLElement>) => {
                      setIdItemPauta(item.idItemPauta);
                      dispatch({
                        type: COMPUTE_VOTES_PRESENCE_VOTES,
                        payload: val
                          ? {
                              agencia: props.agencia,
                              flagSemVotos: computeVote,
                              idPauta: props.idPauta,
                              itens: props.itens.map((x) => {
                                return {
                                  idItemPauta: x.idItemPauta,
                                  quantidade:
                                    item.idItemPauta === x.idItemPauta
                                      ? props.totalAssociados
                                      : 0,
                                  flagAclamacao:
                                    item.idItemPauta === x.idItemPauta
                                      ? aclamacao
                                      : false,
                                };
                              }),
                              nucleo: props.nucleo,
                            }
                          : undefined,
                      });
                      setAclamacaoValue(props.totalAssociados);
                    }}
                    key={index}
                    className={`${rootClassName}-votes-container`}
                  >
                    <RadioButton
                      data-testid={`radio-vote-${index}`}
                      theme={RadioButtonType.New}
                      value={idItemPauta === item.idItemPauta}
                      disabled={props.registerAllNucleous}
                      onChange={(val: boolean) => {
                        setIdItemPauta(item.idItemPauta);
                        dispatch({
                          type: COMPUTE_VOTES_PRESENCE_VOTES,
                          payload: val
                            ? {
                                agencia: props.agencia,
                                flagSemVotos: computeVote,
                                idPauta: props.idPauta,
                                itens: props.itens.map((x) => {
                                  return {
                                    idItemPauta: x.idItemPauta,
                                    quantidade:
                                      item.idItemPauta === x.idItemPauta
                                        ? props.totalAssociados
                                        : 0,
                                    flagAclamacao:
                                      item.idItemPauta === x.idItemPauta
                                        ? aclamacao
                                        : false,
                                  };
                                }),
                                nucleo: props.nucleo,
                              }
                            : undefined,
                        });
                        setAclamacaoValue(props.totalAssociados);
                      }}
                    />
                    <div className={`${rootClassName}-votes-options`}>
                      <strong>{item.descricao}</strong>
                      <strong>
                        {STATUS_LIST_VOTES.IMPORTADA && !aclamacaoValue
                          ? item.quantidadeVotos
                          : idItemPauta === item.idItemPauta
                          ? aclamacaoValue
                          : 0}
                      </strong>
                    </div>
                  </Col>
                );
              })
            ) : (
              <Row>
                {props.itens?.map((item, index: number) => {
                  return (
                    <Col key={index}>
                      <Input
                        data-testid={`vote-input-${index}`}
                        value={
                          inputValue[index]?.quantidade
                            ? inputValue[index]?.quantidade
                            : props.status === STATUS_LIST_VOTES.CONCLUIDA ||
                              props.status === STATUS_LIST_VOTES.INICIADA ||
                              props.status === STATUS_LIST_VOTES.IMPORTADA
                            ? item.quantidadeVotos!
                            : 0
                        }
                        onChange={(
                          evt: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          handleOnChange(evt, index, item.idItemPauta);
                        }}
                        label={item.descricao}
                        placeholder={item.descricao}
                        disabled={(() => {
                          if (props.disabledAclamacao) {
                            return (
                              semManifestacaoVoto ||
                              (props.status === STATUS_LIST_VOTES.INICIADA &&
                                props.ldap !== usuario?.ldap) ||
                              props.registerAllNucleous
                            );
                          } else {
                            return (
                              props.isReadOnly ||
                              (props.status === STATUS_LIST_VOTES.INICIADA &&
                                props.ldap !== usuario?.ldap) ||
                              props.registerAllNucleous
                            );
                          }
                        })()}
                        error={
                          state.presenceVote &&
                          sumAssociados(state.presenceVote!?.itens!) >
                            props.totalAssociados
                        }
                        helperText={
                          state.presenceVote &&
                          sumAssociados(state.presenceVote!?.itens!) >
                            props.totalAssociados
                            ? 'O nº de votos não pode ser maior que o nº de associados aptos'
                            : ''
                        }
                        inputMode={'numeric'}
                        onKeyPress={(event) => {
                          if (!/[0-9]/.test(event.key)) {
                            event.preventDefault();
                          }
                        }}
                      />
                    </Col>
                  );
                })}
              </Row>
            )}
          </Row>
          <Row>
            <Col noPaddingTop>
              <p>
                {translation.ComputeVotes.totalAptos} {props.totalAssociados}
              </p>
            </Col>
            {!aclamacao && !props.disabledNoVote ? (
              <Row>
                <Col className={`${rootClassName}-checkbox-container`}>
                  <ControlLabel
                    control={
                      <Checkbox
                        data-testid={`checked-all-account`}
                        value={computeVote}
                        onChange={(value) => {
                          hasComputeVote(value);
                          setComputeVoteIsDirty(true);
                          handleCheckAvailability();
                        }}
                        disabled={
                          props.isReadOnly ||
                          (props.status !== STATUS_LIST_VOTES.IMPORTADA &&
                            sumAssociados(props.itens, true) > 0) ||
                          (props.status === STATUS_LIST_VOTES.INICIADA &&
                            props.ldap !== usuario!?.ldap) ||
                          props.registerAllNucleous
                        }
                      />
                    }
                    label={translation.ComputeVotes.computeVote}
                  />
                </Col>
              </Row>
            ) : undefined}
            {props.disabledAclamacao &&
            !props.itens.some((x) => x.quantidadeVotos! >= 1) &&
            defaultValues &&
            !defaultValues!.some((x) => x.quantidadeVotos! >= 1) ? (
              <Row>
                <Col className={`${rootClassName}-checkbox-container`}>
                  <ControlLabel
                    control={
                      <Checkbox
                        data-testid={`sem-manifestacao`}
                        value={semManifestacaoVoto}
                        onChange={(e) => {
                          hasComputeVote(false);
                          setSemManifestacaoVoto(e);
                          setManifestacaoVotoIsDirty(true);
                        }}
                        disabled={
                          props.isReadOnly ||
                          (props.status === STATUS_LIST_VOTES.INICIADA &&
                            props.ldap !== usuario!?.ldap) ||
                          props.registerAllNucleous
                        }
                      />
                    }
                    label={translation.ComputeVotes.semManifestacao}
                  />
                </Col>
              </Row>
            ) : (
              <Fragment />
            )}
          </Row>
          <Row>
            <footer className={`${rootClassName}-button-container`}>
              {!aclamacao && !props.disabledAclamacao ? (
                <div>
                  <Button
                    onClick={() => setAclamacao(true)}
                    leftIcon={<DialogAlert />}
                    theme={ButtonThemes.Secondary}
                    className={'btn-aclamacao'}
                    disabled={
                      props.isReadOnly ||
                      (props.status === STATUS_LIST_VOTES.INICIADA &&
                        props.ldap !== usuario!?.ldap) ||
                      props.registerAllNucleous
                    }
                  >
                    {`${translation.ComputeVotes.buttons.aclamacao}`}
                  </Button>
                </div>
              ) : undefined}
              <div className={'w100'}>
                <Button
                  onClick={() => {
                    setDisponibility(true);
                    if (!disponibility) {
                      allowAvailability({
                        agencia: props.agencia,
                        idPauta: props.idPauta,
                        nucleo: props.nucleo,
                      });
                    }

                    if (!aclamacao) {
                      handleToggle();
                    } else {
                      setIdItemPauta(0);
                      setAclamacao(false);
                      setAclamacaoValue(0);
                    }
                  }}
                  leftIcon={<TimesCircleIcon />}
                  theme={ButtonThemes.Secondary}
                  disabled={props.isReadOnly}
                >
                  {aclamacao
                    ? translation.ComputeVotes.buttons.cancelAclamacao
                    : translation.ComputeVotes.buttons.cancel}
                </Button>
                <Button
                  onClick={handleSaveVote}
                  loading={loadingVoto}
                  leftIcon={<CheckCircleIcon />}
                  theme={ButtonThemes.Primary}
                  disabled={props.isReadOnly || disabledSave}
                >
                  {`${translation.ComputeVotes.buttons.save}`}
                </Button>
              </div>
            </footer>
          </Row>
        </Collapse>
      </Row>
    </Row>
  );
};

export default ListVotes;
